import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Input } from "../components/ui/input";
import { Search, Check, ArrowRight, Filter, Users, Camera, CalendarHeart, X } from "lucide-react";
import { formatRupiah } from "../lib/format";
import { distinctiveItems } from "../lib/packages";
import Seo from "../components/Seo";
import Reveal from "../components/Reveal";
import PublicNav from "../components/PublicNav";
import PublicFooter from "../components/PublicFooter";

const publicApi = axios.create({ baseURL: process.env.REACT_APP_BACKEND_URL + "/api" });
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (u) => (u && u.startsWith("/api/") ? `${BACKEND_URL}${u}` : u);

const CATEGORIES = [
  { key: "all", label: "Semua" },
  { key: "Wedding", label: "Wedding" },
  { key: "Akad", label: "Akad" },
  { key: "Khitan", label: "Khitan" },
  { key: "Prewedding", label: "Prewedding" },
];

const BUDGET_BUCKETS = [
  { key: "all", label: "Semua Budget", min: 0, max: Infinity },
  { key: "u10m", label: "< Rp10jt", min: 0, max: 10000000 },
  { key: "10m-25m", label: "Rp10 – 25jt", min: 10000000, max: 25000000 },
  { key: "25m-50m", label: "Rp25 – 50jt", min: 25000000, max: 50000000 },
  { key: "50m+", label: "> Rp50jt", min: 50000000, max: Infinity },
];

const FALLBACK_IMG =
  "https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=1200&q=80";

const STORAGE_KEY = "dwg_paket_wedding_state";

// Landing page for "Paket Wedding DWG" — hanya paket bundling CV Dessy Wedding Group.
export default function PaketWeddingDWG() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const restored = useMemo(() => {
    try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "{}"); }
    catch { return {}; }
  }, []);
  const [cat, setCat] = useState(restored.cat || "all");
  const [budget, setBudget] = useState(restored.budget || "all");
  const [q, setQ] = useState(params.get("q") ?? restored.q ?? "");
  const highlights = useMemo(() => distinctiveItems(items), [items]);

  useEffect(() => {
    setLoading(true);
    publicApi
      .get("/public/catalog", { params: { bundling: true } })
      .then(({ data }) => setItems(data || []))
      .finally(() => setLoading(false));
  }, []);

  // Persist filter + scroll before leaving so back navigation restores context
  useEffect(() => {
    const save = () => {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ cat, budget, q, scrollY: window.scrollY })
      );
    };
    window.addEventListener("beforeunload", save);
    return () => { save(); window.removeEventListener("beforeunload", save); };
  }, [cat, budget, q]);

  // Restore scroll after items load
  useEffect(() => {
    if (!loading && restored.scrollY) {
      window.scrollTo({ top: restored.scrollY, behavior: "instant" });
    }
  }, [loading]);  // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    let list = items;
    if (cat !== "all") list = list.filter((p) => p.event_category === cat);
    if (budget !== "all") {
      const b = BUDGET_BUCKETS.find((x) => x.key === budget);
      if (b)
        list = list.filter((p) => {
          const price = p.current_price ?? p.normal_price ?? 0;
          return price >= b.min && price <= b.max;
        });
    }
    if (q.trim()) {
      const s = q.toLowerCase();
      list = list.filter(
        (p) =>
          (p.product_name || "").toLowerCase().includes(s) ||
          (p.brand || "").toLowerCase().includes(s) ||
          (p.product_family || "").toLowerCase().includes(s) ||
          (p.short_description || "").toLowerCase().includes(s)
      );
    }
    return list;
  }, [items, cat, budget, q]);

  return (
    <div
      className="min-h-screen bg-[#F7F1E8] text-[#1E1611] w-full max-w-full overflow-x-hidden"
      style={{ fontFamily: "'Inter', sans-serif" }}
      data-testid="paket-wedding-dwg-page"
      data-public-page="true"
    >
      <style>{`
        .font-serif { font-family: 'Cormorant Garamond', serif; }
        .gold-line { background: linear-gradient(90deg, transparent 0%, #C4A57B 20%, #C4A57B 80%, transparent 100%); height: 1px; }
        .luxury-shadow { box-shadow: 0 20px 60px -20px rgba(139, 90, 42, 0.35); }
      `}</style>

      <Seo title="Paket Wedding DWG — All-in, Akad, Intimade & Khitan" path="/paket-wedding-dwg"
        description="Daftar lengkap paket wedding bundling Dessy Wedding Group: all-in indoor & outdoor, akad, Intimade 50–300 pax, siraman, khitan & prewedding. Harga transparan, konsultasi gratis." />
      <PublicNav />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[#E5D9C4]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 md:py-20 text-center">
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-8 h-px bg-[#C4A57B]" />
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#8B5A2A] font-semibold">
              Bundling Paket
            </span>
            <div className="w-8 h-px bg-[#C4A57B]" />
          </div>
          <h1 className="font-serif font-medium text-[36px] md:text-[56px] leading-[1.05] tracking-tight">
            Paket Wedding <span className="italic text-[#8B5A2A]">DWG</span>
          </h1>
          <div className="mt-4 gold-line max-w-xs mx-auto" />
          <p className="mt-5 max-w-2xl mx-auto text-[#5C4A3D] font-light text-sm md:text-base">
            Paket lengkap CV Dessy Wedding Group yang menggabungkan beberapa
            layanan dalam satu bundling — MUA, dekorasi, foto & video, hingga
            organizer — supaya perencanaan acara Anda lebih mudah dan hemat.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-10">
        <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
          <div className="flex gap-2 flex-wrap">
            {CATEGORIES.map((c) => (
              <button
                key={c.key}
                onClick={() => setCat(c.key)}
                data-testid={`pw-cat-${c.key.toLowerCase()}`}
                className={`px-4 py-2 rounded-full text-[11px] uppercase tracking-[0.15em] font-semibold transition-all ${
                  cat === c.key
                    ? "bg-[#1E1611] text-[#F7F1E8]"
                    : "bg-white border border-[#E5D9C4] text-[#5C4A3D] hover:border-[#8B5A2A]"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8B5A2A]" />
            <Input
              placeholder="Cari paket / brand..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 bg-white border-[#E5D9C4] rounded-full h-11"
              data-testid="pw-search"
            />
            {q && (
              <button
                onClick={() => setQ("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8B5A2A]"
                aria-label="Bersihkan"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap items-center pb-2">
          <Filter className="w-3.5 h-3.5 text-[#8B5A2A]" />
          <span className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#5C4A3D]">
            Budget:
          </span>
          {BUDGET_BUCKETS.map((b) => (
            <button
              key={b.key}
              onClick={() => setBudget(b.key)}
              data-testid={`pw-budget-${b.key}`}
              className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-widest font-medium transition-all ${
                budget === b.key
                  ? "bg-[#8B5A2A] text-white"
                  : "bg-white border border-[#E5D9C4] text-[#5C4A3D] hover:border-[#8B5A2A]"
              }`}
            >
              {b.label}
            </button>
          ))}
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-8 pb-16">
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="pw-skeleton">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-white rounded-2xl overflow-hidden luxury-shadow animate-pulse">
                <div className="h-52 bg-[#EFE6D3]"/>
                <div className="p-5 space-y-2">
                  <div className="h-3 bg-[#EFE6D3] rounded w-2/3"/>
                  <div className="h-3 bg-[#EFE6D3] rounded w-1/2"/>
                  <div className="h-6 bg-[#EFE6D3] rounded w-1/3 mt-4"/>
                </div>
              </div>
            ))}
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="text-center py-16 text-[#8B5A2A]">
            <p className="text-lg font-serif italic">
              Belum ada paket yang sesuai filter.
            </p>
            <button
              onClick={() => { setCat("all"); setBudget("all"); setQ(""); }}
              className="mt-4 text-xs uppercase tracking-widest underline text-[#8B5A2A]"
              data-testid="pw-clear-filters"
            >
              Bersihkan filter
            </button>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((p, i) => (
            <Reveal key={p.product_id} delay={(i % 3) * 0.08}>
              <PackageCard
                p={p}
                items={highlights(p)}
                onOpen={() => nav(`/catalog/${p.product_id}?from=dwg`)}
              />
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA Konsultasi */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-20">
        <div className="rounded-3xl bg-[#1E1611] text-[#F7F1E8] px-8 py-12 md:px-14 md:py-16 text-center">
          <CalendarHeart className="w-8 h-8 mx-auto mb-3 text-[#C4A57B]" />
          <h3 className="font-serif text-3xl md:text-4xl leading-tight">
            Bingung memilih paket yang pas?
          </h3>
          <p className="mt-3 text-[#E5D9C4] font-light max-w-xl mx-auto text-sm">
            Cerita singkat via Konsultasi Online, tim kami bantu rekomendasikan
            paket bundling yang sesuai kebutuhan dan budget.
          </p>
          <button
            onClick={() => nav("/consultation")}
            className="mt-6 inline-flex items-center gap-2 bg-[#C4A57B] text-[#1E1611] hover:bg-white transition-colors px-8 py-3.5 rounded-full font-semibold text-xs uppercase tracking-widest"
            data-testid="pw-cta-consult"
          >
            Mulai Konsultasi <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}

function PackageCard({ p, items, onOpen }) {
  const hasPromo = p.promo_active && p.promo_price > 0;
  return (
    <button
      onClick={onOpen}
      className="text-left bg-white rounded-2xl overflow-hidden luxury-shadow hover:-translate-y-1 transition-all duration-500 group w-full flex flex-col h-full"
      data-testid={`pw-card-${p.product_id}`}
    >
      <div className="relative h-52 overflow-hidden">
        <img
          src={resolveImg(p.card_image) || FALLBACK_IMG}
          alt={p.product_name}
          className="w-full h-full group-hover:scale-110 transition-transform duration-700 object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
        <div className="absolute top-3 left-3 flex flex-col gap-1">
          {p.event_category && (
            <span className="text-[9px] uppercase tracking-[0.2em] bg-white/95 text-[#1E1611] px-3 py-1.5 rounded-full font-semibold">
              {p.event_category}
            </span>
          )}
          <span className="text-[9px] uppercase tracking-[0.2em] bg-[#8B5A2A] text-white px-3 py-1.5 rounded-full font-semibold">
            Bundling
          </span>
          {hasPromo && (
            <span className="text-[9px] uppercase tracking-[0.2em] bg-rose-600 text-white px-3 py-1.5 rounded-full font-semibold">
              Promo
            </span>
          )}
        </div>
        <div className="absolute bottom-3 left-4 right-4 text-white">
          <div className="font-serif text-2xl leading-tight">{p.product_name}</div>
          {p.brand && (
            <div className="text-[10px] uppercase tracking-widest text-[#C4A57B] mt-1">
              {p.brand}
            </div>
          )}
        </div>
      </div>
      <div className="p-5 flex-1 flex flex-col">
        {p.short_description && (
          <p className="text-xs text-[#5C4A3D] line-clamp-2 min-h-[32px] font-light">
            {p.short_description}
          </p>
        )}
        {items.length > 0 && (
          <div className="mt-3 space-y-1" data-testid={`pw-card-items-${p.product_id}`}>
            {items.slice(0, 4).map((it, i) => (
              <div key={i} className="flex items-start gap-1.5 text-[11px] text-[#3A2E28]">
                <Check className="w-3 h-3 text-[#8B5A2A] shrink-0 mt-0.5" />
                <span className="line-clamp-1">{it}</span>
              </div>
            ))}
            {p.included_items.length > 4 && (
              <div className="text-[10px] text-[#8B5A2A]">
                + item standar DWG & {Math.max(0, p.included_items.length - 4)} item lainnya
              </div>
            )}
          </div>
        )}
        {(p.guest_capacity > 0 || p.crew_count > 0) && (
          <div className="mt-3 flex gap-3 text-[10px] text-[#8B5A2A]">
            {p.guest_capacity > 0 && (
              <span className="flex items-center gap-1">
                <Users className="w-3 h-3" /> Hingga {p.guest_capacity} tamu
              </span>
            )}
            {p.crew_count > 0 && (
              <span className="flex items-center gap-1">
                <Camera className="w-3 h-3" /> {p.crew_count} crew
              </span>
            )}
          </div>
        )}
        <div className="mt-5 pt-4 border-t border-[#F0EBE0] flex items-end justify-between mt-auto">
          <div>
            <div className="text-[9px] uppercase tracking-[0.2em] text-[#8B5A2A] font-semibold">
              {hasPromo ? "Harga promo" : "Harga paket"}
            </div>
            {hasPromo ? (
              <>
                <div className="text-[11px] text-[#8C7A5C] line-through">
                  {formatRupiah(p.normal_price)}
                </div>
                <div className="font-serif font-medium text-rose-700 text-2xl" data-testid={`pw-card-price-${p.product_id}`}>
                  {formatRupiah(p.promo_price)}
                </div>
              </>
            ) : (
              <div className="font-serif font-medium text-[#1E1611] text-2xl" data-testid={`pw-card-price-${p.product_id}`}>
                {formatRupiah(p.normal_price)}
              </div>
            )}
          </div>
          <span className="text-[10px] uppercase tracking-widest text-[#8B5A2A] group-hover:translate-x-1 transition-transform inline-flex items-center gap-1 font-semibold">
            Detail <ArrowRight className="w-3 h-3" />
          </span>
        </div>
      </div>
    </button>
  );
}
